import { useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Building, CheckCircle2, CalendarPlus, Pencil, Tags, TrendingDown } from "lucide-react";

import type { Account } from "../../services/financialService";
import { inventoryService, type Asset, type ItemCategory, type MaintenanceRecord, type Supplier } from "../../services/inventoryService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import AccountSelect from "../financial/AccountSelect";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { MaintenanceForm } from "./AssetMaintenancePanel";
import { ReasonPanel, errorLine } from "./InventoryParts";
import {
  ASSET_STATUSES,
  ASSET_TYPES,
  CONDITIONS,
  assetConfig,
  assetMoves,
  assetStatus,
  day,
  isGone,
  labelFrom,
  ledgerStatus,
  maintenanceStatus,
  money,
  warranty,
} from "./inventoryLogic";

function AssetForm({ asset, categories, suppliers, onClose, onDone }: { asset?: Asset; categories: ItemCategory[]; suppliers: Supplier[]; onClose: () => void; onDone: (asset: Asset) => void }) {
  const locked = Boolean(asset?.is_capitalized);
  const save = useMutation({
    mutationFn: (data: Partial<Asset>) => (asset ? inventoryService.updateAsset(asset.id, data) : inventoryService.createAsset(data)),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    const data: Partial<Asset> = {
      name: text("name"),
      asset_tag: text("asset_tag").toUpperCase(),
      asset_type: text("asset_type") as Asset["asset_type"],
      category: Number(text("category")),
      serial_number: text("serial_number") || null,
      model_number: text("model_number") || null,
      manufacturer: text("manufacturer") || null,
      supplier: text("supplier") ? Number(text("supplier")) : null,
      warranty_expiry: text("warranty_expiry") || null,
      location: text("location") || null,
      condition: text("condition") as Asset["condition"],
      description: text("description") || null,
      notes: text("notes") || null,
    };
    // Once on the ledger, the value fields are the books' business.
    if (!locked) Object.assign(data, { purchase_price: text("purchase_price"), purchase_date: text("purchase_date"), depreciation_rate: text("depreciation_rate") || "0", current_value: text("current_value") || null });
    save.mutate(data);
  };
  const value = (name: keyof Asset) => (asset?.[name] as string | number | null | undefined) ?? "";
  return (
    <FormModal
      id="asset"
      title={asset ? `Edit ${asset.name}` : "Register an asset"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The asset could not be saved.") : undefined}
      saveLabel={asset ? "Save changes" : "Register it"}
      note={asset ? undefined : "The CFO puts it on the ledger from here once it is registered."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The asset" divided={false}>
        <Field label="Name" hint="e.g. Staffroom laptop, Science lab microscope.">
          <input required name="name" maxLength={200} defaultValue={value("name")} className="input mt-1" />
        </Field>
        <Field label="Tag" hint="The number on its sticker; no two share one.">
          <input required name="asset_tag" maxLength={50} defaultValue={value("asset_tag")} className="input mt-1 uppercase" placeholder="e.g. ICT-0042" />
        </Field>
        <Field label="Type">
          <select name="asset_type" defaultValue={value("asset_type") || "equipment"} className="input mt-1">
            {ASSET_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Category">
          <select required name="category" defaultValue={value("category")} className="input mt-1">
            <option value="">Choose a category</option>
            {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
          </select>
        </Field>
        <Field label="Kept at" optional hint="Room or building.">
          <input name="location" maxLength={200} defaultValue={value("location")} className="input mt-1" />
        </Field>
        <Field label="Condition">
          <select name="condition" defaultValue={value("condition") || "good"} className="input mt-1">
            {CONDITIONS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
      </FormSection>

      <FormSection title="Value" description={locked ? "On the ledger: these are fixed; depreciation and disposal change them." : "Depreciation is posted by the CFO at this yearly rate."}>
        <Field label="Bought on">
          <input required={!locked} disabled={locked} type="date" name="purchase_date" max={todayIso()} defaultValue={value("purchase_date")} className="input mt-1" />
        </Field>
        <Field label="Price (UGX)">
          <input required={!locked} disabled={locked} type="number" name="purchase_price" min={0} step="any" defaultValue={value("purchase_price")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Depreciation (% a year)" optional hint="e.g. 25 for computers, 10 for furniture.">
          <input disabled={locked} type="number" name="depreciation_rate" min={0} max={100} step="0.01" defaultValue={value("depreciation_rate") || 0} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Worth now (UGX)" optional hint="Only if it differs from the price.">
          <input disabled={locked} type="number" name="current_value" min={0} step="any" defaultValue={value("current_value")} className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>

      <FormSection title="Make and supplier" columns={3}>
        <Field label="Made by" optional>
          <input name="manufacturer" maxLength={200} defaultValue={value("manufacturer")} className="input mt-1" />
        </Field>
        <Field label="Model" optional>
          <input name="model_number" maxLength={100} defaultValue={value("model_number")} className="input mt-1" />
        </Field>
        <Field label="Serial number" optional>
          <input name="serial_number" maxLength={100} defaultValue={value("serial_number")} className="input mt-1" />
        </Field>
        <Field label="Bought from" optional>
          <select name="supplier" defaultValue={value("supplier")} className="input mt-1">
            <option value="">Not recorded</option>
            {suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
          </select>
        </Field>
        <Field label="Warranty ends" optional>
          <input type="date" name="warranty_expiry" defaultValue={value("warranty_expiry")} className="input mt-1" />
        </Field>
        <Field label="Description" optional className="sm:col-span-2 lg:col-span-3">
          <textarea name="description" rows={2} defaultValue={value("description")} className="input mt-1" />
        </Field>
        <Field label="Notes" optional className="sm:col-span-2 lg:col-span-3">
          <textarea name="notes" rows={2} defaultValue={value("notes")} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/**
 * The fixed-asset register. The inventory manager keeps it; whoever posts to
 * the ledger (the CFO) puts assets on the books, depreciates and disposes.
 */
export function AssetsPanel({ assets, categories, suppliers, maintenance, accounts, keepsRegister, postsAssets, isLoading, isError, onRetry, onChanged, onOpenCategories }: {
  assets?: Asset[];
  categories: ItemCategory[];
  suppliers: Supplier[];
  maintenance: MaintenanceRecord[];
  accounts?: Account[];
  keepsRegister: boolean;
  postsAssets: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  onOpenCategories: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<Asset | "new" | null>(null);
  const [booking, setBooking] = useState(false);
  const [nextStatus, setNextStatus] = useState("");
  const [payFrom, setPayFrom] = useState("");
  const [asOf, setAsOf] = useState(today);
  const [disposal, setDisposal] = useState({ date: today, proceeds: "0", account: "" });
  const history = useQuery({ queryKey: ["asset-status-history", viewing], queryFn: () => inventoryService.getAssetStatusHistory(viewing!), enabled: viewing !== null });
  const done = (text: string) => {
    setNotice({ tone: "success", text });
    history.refetch();
    onChanged();
  };
  const move = useMutation({ mutationFn: ({ id, status, reason }: { id: number; status: Asset["status"]; reason: string }) => inventoryService.transitionAsset(id, status, reason), onSuccess: (row) => done(`${row.name} is now ${labelFrom(ASSET_STATUSES, row.status).toLowerCase()}.`) });
  const capitalize = useMutation({ mutationFn: ({ id, account }: { id: number; account: number }) => inventoryService.capitalizeAsset(id, account), onSuccess: (row) => done(`${row.name} is on the ledger at ${money(row.purchase_price)}.`) });
  const depreciate = useMutation({ mutationFn: ({ id, date }: { id: number; date: string }) => inventoryService.depreciateAsset(id, date), onSuccess: (row) => done(`Depreciation posted; ${row.name} is now worth ${money(row.net_book_value)}.`) });
  const dispose = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      inventoryService.disposeAsset(id, { disposal_date: disposal.date, proceeds: disposal.proceeds || "0", receipt_account: Number(disposal.proceeds) > 0 ? Number(disposal.account) : undefined, reason }),
    onSuccess: (row) => done(`${row.name} is written off the ledger.`),
  });

  const columns: RegisterColumn<Asset>[] = [
    { header: "Tag", cell: (row) => <span className="font-medium text-gray-900">{row.asset_tag}</span>, exportValue: (row) => row.asset_tag, className: "whitespace-nowrap" },
    { header: "Asset", cell: (row) => row.name, exportValue: (row) => row.name, className: "min-w-[12rem] text-gray-900" },
    { header: "Type", cell: (row) => labelFrom(ASSET_TYPES, row.asset_type), exportValue: (row) => labelFrom(ASSET_TYPES, row.asset_type), className: "text-gray-700" },
    { header: "Kept at", cell: (row) => row.location || <span className="text-gray-300">—</span>, exportValue: (row) => row.location ?? "", className: "text-gray-700" },
    { header: "Bought", cell: (row) => day(row.purchase_date), exportValue: (row) => day(row.purchase_date), exportOnly: true },
    { header: "Price", cell: (row) => money(row.purchase_price), exportValue: (row) => Number(row.purchase_price), align: "right" },
    { header: "Book value", cell: (row) => money(row.net_book_value), exportValue: (row) => Number(row.net_book_value), align: "right" },
    { header: "Condition", cell: (row) => labelFrom(CONDITIONS, row.condition), exportValue: (row) => labelFrom(CONDITIONS, row.condition), className: "text-gray-700" },
    { header: "Ledger", cell: (row) => <StatusDot {...ledgerStatus(row)} />, exportValue: (row) => ledgerStatus(row).label },
    { header: "Status", cell: (row) => <StatusDot {...assetStatus(row)} />, exportValue: (row) => assetStatus(row).label },
  ];

  const asset = viewing !== null ? assets?.find((row) => row.id === viewing) : undefined;
  const jobs = asset ? maintenance.filter((row) => row.asset === asset.id) : [];
  const moves = asset ? assetMoves(asset) : [];
  const cover = asset ? warranty(asset, today) : null;
  const canPost = postsAssets && asset && !asset.disposal_date;
  const payable = (account: Account) => account.is_active && (account.account_type === "liability" || (account.account_type === "asset" && account.can_pay_from === true));
  const receivesMoney = (account: Account) => account.is_active && account.account_type === "asset" && account.can_pay_from === true;
  const noAccounts = (test: (account: Account) => boolean) => accounts !== undefined && !accounts.some(test);

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={postsAssets && !keepsRegister ? "ledger" : "register"}
        title="Assets"
        description={keepsRegister ? "Open an asset for its details, status history and repairs." : "Open an asset to put it on the ledger, post depreciation or record its disposal."}
        actions={keepsRegister && (
          <>
            <button type="button" onClick={onOpenCategories} className="btn btn-outline h-8 min-h-8 gap-1.5 px-3 text-xs">
              <Tags className="h-3.5 w-3.5" /> Categories
            </button>
            <button type="button" disabled={!categories.length} onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
              <Building className="mr-1.5 h-3.5 w-3.5" /> Register an asset
            </button>
          </>
        )}
        rows={assets}
        config={assetConfig(today)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          [move, capitalize, depreciate, dispose].forEach((mutation) => mutation.reset());
          setNextStatus("");
          setAsOf(today);
          setDisposal({ date: today, proceeds: "0", account: "" });
          setViewing(row.id);
        }}
        openLabel={(row) => (postsAssets && !row.is_capitalized && !isGone(row) ? "Put on ledger" : "Open")}
        noun={["asset", "assets"]}
        exportName={{ file: "fixed-assets", title: "Fixed Asset Register" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={keepsRegister ? "No assets registered yet." : "The inventory manager has not registered any assets yet."}
        initialState={postsAssets && !keepsRegister ? { view: "unposted" } : undefined}
      />

      {asset && !editing && !booking && (
        <DetailsModal
          size="3xl"
          title={`${asset.asset_tag} · ${asset.name}`}
          headline={{ label: "Book value", value: money(asset.net_book_value), note: `Bought ${day(asset.purchase_date)} for ${money(asset.purchase_price)}${Number(asset.depreciation_rate) ? ` · ${Number(asset.depreciation_rate)}% a year` : ""}` }}
          status={assetStatus(asset)}
          notice={
            <div className="space-y-2">
              <p className="flex flex-wrap items-center gap-2 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <StatusDot {...ledgerStatus(asset)} />
                {asset.is_capitalized
                  ? `Put on the ledger ${day(asset.capitalized_at)}${asset.capitalized_by_name ? ` by ${asset.capitalized_by_name}` : ""}${asset.last_depreciated_on ? `; depreciated to ${day(asset.last_depreciated_on)}` : "; no depreciation posted yet"}.`
                  : isGone(asset)
                    ? "It never went on the ledger."
                    : "Not in the books yet: the CFO puts it on the ledger."}
              </p>
              {cover && (cover.expired || cover.soon) && !isGone(asset) && (
                <p className={`rounded-md px-3 py-2 text-sm ${cover.expired ? "bg-gray-50 text-gray-700" : "bg-amber-50 text-amber-900"}`}>
                  Warranty {cover.expired ? `ended ${cover.expiry}` : `ends ${cover.expiry}, in ${cover.days} days`}.
                </p>
              )}
            </div>
          }
          facts={[
            ["Type", labelFrom(ASSET_TYPES, asset.asset_type)],
            ["Category", asset.category_name],
            ["Kept at", asset.location ?? undefined],
            ["With", asset.assigned_to_name],
            ["Condition", labelFrom(CONDITIONS, asset.condition)],
            ["Made by", [asset.manufacturer, asset.model_number].filter(Boolean).join(" ") || undefined],
            ["Serial number", asset.serial_number ?? undefined],
            ["Bought from", asset.supplier_name],
            ["Warranty ends", day(asset.warranty_expiry) || undefined],
            ["Last service", day(asset.last_maintenance_date) || undefined],
            ["Next service", day(asset.next_maintenance_date) || undefined],
            ["Disposed", asset.disposal_date ? `${day(asset.disposal_date)} for ${money(asset.disposal_proceeds) || "UGX 0"}` : undefined],
          ]}
          sections={[
            { title: "Description", body: asset.description },
            { title: "Notes", body: asset.notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {keepsRegister && !isGone(asset) && (
                <button type="button" className="btn btn-outline gap-1.5" onClick={() => setBooking(true)}>
                  <CalendarPlus className="h-4 w-4" /> Book work
                </button>
              )}
              {keepsRegister && !isGone(asset) && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing(asset)}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        >
          {canPost && !asset.is_capitalized && !isGone(asset) && (
            <ActionPanel title="Put it on the ledger" description={`Debits fixed assets and credits the account it was paid from, for ${money(asset.purchase_price)}.`}>
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                <Field label="Paid from" as="div" hint="Cash or bank, or a payable if it is still owed.">
                  <AccountSelect
                    className="mt-1"
                    placeholder="Choose the account…"
                    accounts={accounts}
                    isSelectable={payable}
                    value={payFrom}
                    onChange={setPayFrom}
                  />
                </Field>
                <button type="button" disabled={!payFrom || capitalize.isPending} onClick={() => capitalize.mutate({ id: asset.id, account: Number(payFrom) })} className="btn btn-primary gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Put on the ledger
                </button>
              </div>
              {noAccounts(payable) && (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  The chart of accounts has no cash, bank or payable account to post against yet. Set one up under Financial, Chart of accounts.
                </p>
              )}
              {capitalize.isError && errorLine(capitalize.error, "It could not be put on the ledger.")}
            </ActionPanel>
          )}

          {canPost && asset.is_capitalized && (
            <ActionPanel
              title="Post depreciation"
              description={Number(asset.depreciation_rate) ? `At ${Number(asset.depreciation_rate)}% a year, from ${day(asset.last_depreciated_on) || day(asset.capitalized_at)} to the date below.` : "Set a yearly rate on the asset first."}
            >
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                <Field label="Up to">
                  <input type="date" value={asOf} min={asset.last_depreciated_on ?? undefined} max={today} onChange={(event) => setAsOf(event.target.value)} className="input mt-1" />
                </Field>
                <button type="button" disabled={!Number(asset.depreciation_rate) || !asOf || depreciate.isPending} onClick={() => depreciate.mutate({ id: asset.id, date: asOf })} className="btn btn-outline gap-1.5">
                  <TrendingDown className="h-4 w-4" /> Post depreciation
                </button>
              </div>
              {depreciate.isError && errorLine(depreciate.error, "Depreciation could not be posted.")}
            </ActionPanel>
          )}

          {canPost && asset.is_capitalized && (
            <ReasonPanel
              title="Dispose of it"
              description={`Writes off the book value of ${money(asset.net_book_value)} against anything received for it.`}
              placeholder="e.g. Sold at the end of its life; replaced by a new one."
              context={{ key: "inventory.note", params: { kind: "disposal", id: asset.id } }}
              button="Dispose of it"
              danger
              busy={dispose.isPending}
              error={dispose.error}
              errorText="The disposal could not be posted."
              onSubmit={(reason) => (Number(disposal.proceeds) > 0 && !disposal.account ? undefined : dispose.mutate({ id: asset.id, reason }))}
            >
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="On">
                  <input type="date" value={disposal.date} max={today} onChange={(event) => setDisposal((current) => ({ ...current, date: event.target.value }))} className="input mt-1" />
                </Field>
                <Field label="Received for it (UGX)">
                  <input type="number" min={0} step="any" value={disposal.proceeds} onChange={(event) => setDisposal((current) => ({ ...current, proceeds: event.target.value }))} className="input mt-1 tabular-nums" />
                </Field>
                {Number(disposal.proceeds) > 0 && (
                  <Field label="Paid into" as="div">
                    <AccountSelect
                      className="mt-1"
                      placeholder="Cash or bank…"
                      accounts={accounts}
                      isSelectable={receivesMoney}
                      value={disposal.account}
                      onChange={(value) => setDisposal((current) => ({ ...current, account: value }))}
                    />
                  </Field>
                )}
              </div>
            </ReasonPanel>
          )}

          {keepsRegister && moves.length > 0 && (
            <ReasonPanel
              key={asset.status}
              title="Change its status"
              description={asset.is_capitalized ? "It is on the ledger, so disposing of it is done by the CFO above." : undefined}
              placeholder="e.g. Taken to the staffroom store after the lab was refitted."
              context={{ key: "inventory.note", params: { kind: "asset_status", id: asset.id, to_status: nextStatus || undefined } }}
              button="Change the status"
              busy={move.isPending}
              error={move.error}
              errorText="The status could not be changed."
              onSubmit={(reason) => nextStatus && move.mutate({ id: asset.id, status: nextStatus as Asset["status"], reason })}
            >
              <Field label="New status">
                <select value={nextStatus} onChange={(event) => setNextStatus(event.target.value)} className="input mt-1">
                  <option value="">Choose…</option>
                  {moves.map((status) => <option key={status} value={status}>{labelFrom(ASSET_STATUSES, status)}</option>)}
                </select>
              </Field>
            </ReasonPanel>
          )}

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Status history</h4>
            {history.isLoading ? (
              <div className="mt-1.5 h-12 animate-pulse rounded bg-gray-100" />
            ) : history.data?.length ? (
              <ul className="mt-1.5 divide-y divide-gray-100 rounded-lg border border-gray-200">
                {history.data.map((change) => (
                  <li key={change.id} className="px-3 py-2 text-sm">
                    <span className="tabular-nums text-gray-500">{day(change.created_at)}</span>
                    <span className="ml-2 font-medium text-gray-900">{labelFrom(ASSET_STATUSES, change.from_status)} → {labelFrom(ASSET_STATUSES, change.to_status)}</span>
                    <span className="ml-2 text-gray-500">by {change.changed_by_name || "—"}</span>
                    <span className="block whitespace-pre-wrap text-gray-700">{change.reason}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1.5 text-sm text-gray-500">In use since it was registered.</p>
            )}
          </section>
          {keepsRegister && (
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Services and repairs ({jobs.length})</h4>
              {jobs.length ? (
                <ul className="mt-1.5 divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {jobs.slice(0, 8).map((job) => (
                    <li key={job.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span>
                        <span className="tabular-nums text-gray-500">{day(job.performed_date ?? job.scheduled_date)}</span>
                        <span className="ml-2 font-medium text-gray-900">{job.title}</span>
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="tabular-nums text-gray-600">{money(job.total_cost)}</span>
                        <StatusDot {...maintenanceStatus(job, today)} />
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1.5 text-sm text-gray-500">None recorded.</p>
              )}
            </section>
          )}
        </DetailsModal>
      )}

      {asset && booking && (
        <MaintenanceForm
          assets={assets ?? []}
          asset={asset}
          onClose={() => setBooking(false)}
          onDone={(saved) => {
            setBooking(false);
            done(`Work on ${saved.asset_name} booked for ${day(saved.scheduled_date)}. Follow it under Maintenance.`);
          }}
        />
      )}
      {editing && (
        <AssetForm
          asset={editing === "new" ? undefined : editing}
          categories={categories}
          suppliers={suppliers}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            const created = editing === "new";
            setEditing(null);
            setNotice({ tone: "success", text: created ? `${saved.asset_tag} · ${saved.name} registered.` : `${saved.name} updated.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
